/**
 * @module app.AuthenticationService
 */
import appBase from './index.js';
import {deleteCookies} from './cookieshelper.js';
import Client from '@geoblocks/oidcjs';
import {useCookieBasedAuth} from './authMode.js';

const OIDC_CLIENT_ID = 'schweizmobil-website';
const USER_ROLE = {
  subscriptionRenewal: 'subscription-renewal',
  subscribedUser: 'subscribed-user',
  subscriptionPending: 'subscription-pending',
  subscriptionExpired: 'subscription-expired',
  subscriptionCanceled: 'subscription-canceled',
};


/**
 * @param {angular.Scope} $rootScope
 * @param {string} apiUrl API url prefix.
 * @param {angular.$http} $http Http service.
 * @param {angular.$q} $q Angular Q service.
 * @param {gettext} gettext function for dynamic i18n.
 * @param {!ngeo.statemanager.Location} ngeoLocation
 * @param {app.waiting} appWaiting
 * @param {string} sseUrl
 * @constructor
 * @ngInject
 */
class AuthenticationService {
  constructor($rootScope, apiUrl, $http, $q, gettext, ngeoLocation, appWaiting, sseUrl, keycloakIssuer) {

    this.q_ = $q;
    /**
     * @type {EventSource}
     */
    this.evtSource;
    this.sseUrl = sseUrl;

    /**
     * @type {string}
     * @private
     */
    this.apiUrl_ = apiUrl;

    /**
     * @type {angular.$http}
     * @private
     */
    this.http_ = $http;

    /**
     * @type {angular.Scope}
     * @private
     */
    this.rootScope_ = $rootScope;

    /**
     * @private
     */
    this.appWaiting_ = appWaiting;

    /**
     * @type {boolean}
     * @export
     */
    this.loggedIn = false;

    /**
     * @type {string|undefined}
     */
    this.userId;

    /**
     * @type {!Object}
     * @export
     */
    this.errorMessages = {};

    /**
     * @type {string}
     * @private
     */
    this.unknownLoginErrorMsg_ =
      gettext('error occured server side on login');

    /**
     * @type {string}
     * @private
     */
    this.unknownLogoutErrorV3Msg_ = gettext('error occured in v3 backend when trying to log out');

    /**
     * @type {string}
     * @private
     */
    this.emptyLoginMsg_ = gettext('login must not be empty');

    /**
     * @type {string}
     * @private
     */
    this.emptyPasswordMsg_ = gettext('password must not be empty');

    /**
     * @type {string}
     * @private
     */
    this.loginDisabledMsg_ = gettext('login service is disabled');

    /**
     * @type {string}
     * @private
     */
    this.sessionExpiredMsg_ = gettext('session has expired');

    /**
     * @type {string}
     * @private
     */
    this.loginAgainMsg_ = gettext('You have been logged out, please log in again.');

    /**
     * @type {string}
     * @private
     */
    this.loginErrorMsg_ = gettext('login service is unavailable message');

    /**
     * @type {string}
     * @private
     */
    this.subscriptionErrorMsg_ = gettext('Your subscription is not valid');

    /**
     * @type {?appx.UserData|undefined}
     * @export
     */
    this.data;

    /**
     * @type {appx.UserLoginConfig}
     * @export
     */
    this.loginConfig;

    this.keycloakIssuer = keycloakIssuer;

    this.subscriptionOk = false;

    /**
     * @private
     */
    this.ngeoLocation_ = ngeoLocation;

    this.oidcClient = new Client(
      {
        redirectUri: location.pathname.includes('index.debug.html') ? `${location.origin}/index.debug.html` : `${location.origin}/`,
        clientId: OIDC_CLIENT_ID,
        pkce: true,
        checkToken: async () => {
          return true;
        },
        debug: true
      },
      {
        authorization_endpoint: `${keycloakIssuer}/protocol/openid-connect/auth`,
        token_endpoint: `${keycloakIssuer}/protocol/openid-connect/token`,
        logout_endpoint: `${keycloakIssuer}/protocol/openid-connect/logout`,
      },
    );

    /**
     * @type {Promise}
     * @export
     */
    this.tokenLoginPromise = useCookieBasedAuth ? this.q_.resolve() : Promise.resolve();

    const postLogoutUrl = localStorage.getItem('appPostLogoutURL');
    if (postLogoutUrl) {
      localStorage.removeItem('appPostLogoutURL');
      document.location = postLogoutUrl;
      return;
    }
    const preLoginUrl = localStorage.getItem('appPreLoginURL');
    if (preLoginUrl) {
      try {
        const searchParams = new URLSearchParams(document.location.search);
        this.oidcClient.handleStateIfInURL(searchParams).then(async (results) => {
          switch (results.status) {
            case 'completed': {
              localStorage.removeItem('appPreLoginURL');
              document.location = preLoginUrl;
              return;
            }
            case 'invalid':
            case 'error': {
              this.updateErrorMsg('login', this.unknownLoginErrorMsg_);
              console.log('Login failed', results.msg);
              return;
            }
            case 'nothing':
            default: {
              // Do nothing
              return;
            }
          }
        });
      } catch (error) {
        this.updateErrorMsg('login', this.unknownLoginErrorMsg_);
        console.log('Login failed', error);
      }
    }

    if (document.cookie.indexOf('undefined') != -1) {
      console.log('Clearing undefined cookie');
      document.cookie = "undefined;expires=" + new Date(0).toUTCString();
      deleteCookies();
    }

    this.http_.get(this.apiUrl_ + '4/loginConfig').then((response) => {
      this.loginConfig = /** @type {appx.UserLoginConfig} */ (response.data);
      if (this.loginConfig.maintenance_from && this.loginConfig.maintenance_to) {
        // Parsing date by suffixing with 'Z': date without timezone
        const fromDate = new Date(this.loginConfig.maintenance_from + 'Z');
        const toDate = new Date(this.loginConfig.maintenance_to + 'Z');
        const now = new Date();
        const delayBefore = Math.max(fromDate - now, 0);
        const delayUntil = Math.max(toDate - now, 0);
        if (delayUntil > 0) {
          const maintenanceOver = () => {
            document.body.classList.remove('in-maintenance');
            console.info('Maintenance is over');
            this.loginConfig.showloginmsg = false;
            this.rootScope_.$broadcast('maintenance', 0);
          };
          const maintenanceBegins = () => {
            console.info('Site is currently in maintenance');
            document.body.classList.add('in-maintenance');
            const remaining = delayUntil - delayBefore;
            // Guess what: using $timeout here prevents the menu from appearing
            // when user is logged and we enter the maintenance period! ????
            // if you understand this AngularJS mystery please let me know, Guillaume
            setTimeout(() => this.rootScope_.$apply(maintenanceOver), remaining);
            $rootScope.$broadcast('maintenance', remaining);
          };
          // idem :/
          setTimeout(() => this.rootScope_.$apply(maintenanceBegins), delayBefore);
          console.info('maintenance planned', delayBefore);
        }
      }
      if (this.loginConfig.logindisabled) {
        this.updateErrorMsg('disabled', this.loginDisabledMsg_);
        this.logout();
      }

      // Sometimes the red menu is empty
      // To avoid this we early call loginChange with some value (a prori false)
      this.rootScope_.$broadcast('loginChange', this.loggedIn);
      this.updateAuthInformation().finally(() => this.rootScope_.$broadcast('loginChange', this.loggedIn));
    }, (error) => {
      // general api unavailable error
      this.loginConfig = {
        logindisabled: true,
        showloginmsg: false,
        loginmsgde: '',
        loginmsgen: '',
        loginmsgfr: '',
        loginmsgit: ''
      };
      this.updateErrorMsg('error', this.loginErrorMsg_);
      this.rootScope_.$broadcast('loginChange', this.loggedIn);
    });

  }

  /**
   * @param {!Object} credentials
   * @param {function()} onSuccess
   */
  loginWithCookie(credentials, onSuccess) {
    this.appWaiting_.increment();
    const successCb = (response) => {
      const data = /** @type {appx.UserData} */ (response.data);
      if (data.loginErrorCode == 200) {
        onSuccess();
      } else {
        this.updateErrorMsg('login', data.loginErrorMsg);
      }
      this.appWaiting_.decrement();
    };
    const errorCb = (response) => {
      this.updateErrorMsg('login', this.unknownLoginErrorMsg_);
      this.appWaiting_.decrement();
    };
    this.http_.post(this.apiUrl_ + '4/login', credentials).then(successCb).catch(errorCb);
  }

  async loginWithKeycloak() {
    this.appWaiting_.increment();
    localStorage.setItem('appPreLoginURL', document.location.href);
    try {
      const loginURL = await this.oidcClient.createAuthorizeAndUpdateLocalStorage([
        'openid',
        'roles',
      ]);
      document.location = loginURL;
      this.appWaiting_.decrement();
    } catch (error) {
      console.error('Error:', error);
      this.updateErrorMsg('login', this.unknownLoginErrorMsg_);
      this.appWaiting_.decrement();
    }
  }

  logoutFromKeycloak(idToken) {
    // Also logout from the SSO itself (not only the app)
    const newLocation = new URL(
      `${this.keycloakIssuer}/protocol/openid-connect/logout`,
    );
    newLocation.searchParams.append(
      'post_logout_redirect_uri',
      document.location.origin + document.location.pathname,
    );
    newLocation.searchParams.append('client_id', OIDC_CLIENT_ID);
    newLocation.searchParams.append('id_token_hint', idToken);
    this.ngeoLocation_.deleteParam('trackId');
    this.ngeoLocation_.deleteParam('recordedTrackId');
    localStorage.setItem('appPostLogoutURL', this.ngeoLocation_.getUriString());
    document.location = newLocation.toString();
  }

  logout() {
    if (useCookieBasedAuth) {
      if (this.evtSource) {
        this.evtSource.close();
      }
      const successCb = (response) => {
        this.loggedIn = false;
        this.userId = undefined;
        this.data = undefined;
        this.rootScope_.$broadcast('logout');
      };
      const errorCb = (response) => {
        this.updateErrorMsg('logout', this.unknownLogoutErrorV3Msg_);
      };
      this.http_.get(this.apiUrl_ + '4/logout').then(successCb).catch(errorCb);

    } else {
      const idToken = localStorage.getItem('oidcjs_id_token');
      if (!idToken) {
        return;
      }
      this.oidcClient.lclear();
      this.loggedIn = false;
      this.userId = undefined;
      this.data = undefined;
      this.rootScope_.$broadcast('logout');
      // we will reload the page during the logout process so we don't have to clear the user status
      // in addition, that would interfere with the logout process
      this.logoutFromKeycloak(idToken);
    }
  }

  /**
   * @export
   */
  delog() {
    this.updateErrorMsg('logout', this.sessionExpiredMsg_);
    this.logout();
  }

  /**
   * @export
   * @param {string} id error id
   * @param {string} msg error message
   * store in an array unique objects corresponding to various error types
   */
  updateErrorMsg(id, msg) {
    if (!msg) {
      delete this.errorMessages[id];
    } else {
      this.errorMessages[id] = msg;
    }
  }

  createNewEventSource_(url) {
    console.log('SSE: creating source');
    this.evtSource = new EventSource(url);
    this.evtSource.onmessage = (event) => {
      console.log(`SSE: ${event.data}`);
      this.rootScope_.$broadcast('SSE_REFRESH');
    };
    this.evtSource.onerror = () => {
      if (this.evtSource.readyState == 2) {
        const oldEvtSource = this.evtSource;
        const waitTime = 4000 + 30000 * Math.random();
        console.log(`SSE: error, list will not update for a ${Math.ceil(waitTime / 1000)}s`);
        setTimeout(() => {
          if (this.evtSource === oldEvtSource && oldEvtSource.readyState == 2) {
            console.log('Trying to resurect SSE');
            this.createNewEventSource_(oldEvtSource.url);
          }
        }, waitTime);
      }
    };
  }

  refreshUpdateTracksSSE(userId) {
    if (!this.sseUrl) {
      console.log('Bypassing SSE registration');
      return;
    }
    const url = `${this.sseUrl}events/${userId}`;
    if (this.evtSource) {
      if (this.evtSource.url === url && this.evtSource.readyState !== 2) {
        console.log('SSE skipping recreating same SSE source');
        return;
      }
      console.log('SSE: closing source');
      this.evtSource.close();
    }
    if (userId) {
      this.createNewEventSource_(url);
    }
  }

  /**
   * Check with the server if the user is logged in and notify the caller.
   * Additionnaly, if the user is not logged in, call logout to reset the
   * interface.
   * @param {function(boolean, boolean)} callback
   */
  isUserLoggedIn(callback) {
    this.appWaiting_.increment();
    if (useCookieBasedAuth) {
      const callbackWrapper = (loggedIn, userId) => {
        this.appWaiting_.decrement();
        this.refreshUpdateTracksSSE(userId);
        if (!loggedIn) {
          this.updateErrorMsg('isUserLoggedIn', this.loginAgainMsg_);
          this.logout();
        }
        callback(loggedIn, loggedIn);
      };
      const successCb = (response) => {
        callbackWrapper(response.status === 200 && response.data['loggedin'], response.data['userid']);
      };
      const errorCb = (response) => {
        callbackWrapper(false, false);
      };
      this.http_.get(this.apiUrl_ + '4/check').then(successCb).catch(errorCb);
    } else {
      this.updateAuthInformation().finally(() => {
        this.appWaiting_.decrement();
        if (!this.loggedIn) {
          this.updateErrorMsg('isUserLoggedIn', this.loginAgainMsg_);
          this.logout();
        }
        callback(this.loggedIn, this.subscriptionOk);
      });
    }
  }

  async updateAuthInformation() {
    if (useCookieBasedAuth) {
      // Ubique token
      const token = this.ngeoLocation_.getParam('token');
      if (token) {
        this.tokenLoginPromise = this.http_.post(this.apiUrl_ + '4/login', {
          'token': token
        });
        this.ngeoLocation_.deleteParam('token');
      } else {
        this.tokenLoginPromise = this.q_.resolve();
      }

      // This is both for Ubique token and cookie log in
      return this.tokenLoginPromise.then(() => {
        return this.http_.get(this.apiUrl_ + '4/check').then((response) => {
          const data = /** @type {appx.UserCheckResponse} */ (response.data);
          this.loggedIn = data.loggedin;
          this.userId = data.userid;
          if (this.loggedIn) {
            this.data = /** @type {!appx.UserData} */ (data);
            this.subscriptionOk = true;
          } else {
            // We clean all cookies if they exist (since they don't work anyway)
            console.log('just in case - clearing cookies');
            deleteCookies();
          }
          this.refreshUpdateTracksSSE(this.loggedIn ? this.userId : undefined);

          this.rootScope_.$broadcast('loginChange', this.loggedIn);
        }).catch((response) => {
          if (response.status >= 400 && response.status < 500) {
            console.log('there is something wrong - clearing cookie');
            deleteCookies();
            this.rootScope_.$broadcast('loginChange', false);
          }
        });
      });
      // return nothing: unused
    } else {
      this.tokenLoginPromise = this.oidcClient.getActiveToken();
      return this.tokenLoginPromise.then(token => {
        const parsedToken = this.oidcClient.parseJwtPayload(token);
        this.loggedIn = !!(token && token !== '' && parsedToken);
        this.userId = undefined;
        this.data = undefined;
        this.subscriptionOk = false;
        if (this.loggedIn) {
          this.userId = parsedToken.uid;
          const roles = parsedToken.roles;
          this.subscriptionOk =
            roles.includes(USER_ROLE.subscribedUser) ||
            roles.includes(USER_ROLE.subscriptionRenewal) ||
            roles.includes(USER_ROLE.subscriptionPending) ||
            roles.includes(USER_ROLE.subscriptionCanceled);
          this.data = {
            email: parsedToken.email,
            loggedin: this.loggedIn,
            name: parsedToken.email,
            userid: this.userId,
          };
          if (!this.subscriptionOk) {
            this.updateErrorMsg('isSubscriptionOK', this.subscriptionErrorMsg_);
          }
        } else {
          this.logout();
        }
        this.refreshUpdateTracksSSE(this.loggedIn && this.subscriptionOk ? this.userId : undefined);
      });
    }
  }
}

export default appBase.service('appAuthenticationService', AuthenticationService);
